import { Link } from "react-router-dom";
import { ArrowRight, Check, Scale } from "lucide-react";
import { InfoPage } from "../components/info/InfoPage";
import { FactorGrid, IllustratedSplit, InfoSection } from "../components/info/InfoSections";
import { CompareTable } from "../components/info/CompareTable";
import { TextBody } from "../components/info/TextBody";
import { CompareLink } from "../components/info/InfoHero";
import { MaterialPrices } from "../components/info/MaterialPrices";
import { ProfileSystemsSection } from "../components/catalog/ProfileSystemsSection";
import { QuoteButton } from "../components/home/CtaButtons";
import { useSiteTexts } from "../lib/siteTexts";
import { PAGE_TEXTS } from "../data/pageTexts";
import { ALUMINIUM_PROFILE, APPLICATIONS_TABLE, CHOOSE, KUNSTSTOF_PROFILE, MAIN_TABLE, MATERIAAL_NAV, MATERIAAL_POINTS, THEMES } from "../data/info/materiaal";

const ChooseCard = ({ c, accent }) => (
  <div className="card flex flex-col p-6 sm:p-8" data-testid={`materiaal-choose-${c.onderwerp}`}>
    <p className={`text-[12px] font-semibold uppercase tracking-[0.18em] ${accent}`}>{c.label}</p>
    <h3 className="mt-2 text-[22px] font-bold text-ink">Kies {c.label.toLowerCase()} als…</h3>
    <ul className="mt-5 grid flex-1 gap-2.5">
      {c.items.map((it) => <li key={it} className="flex items-start gap-3 text-[15px] leading-relaxed text-slate-700"><Check className="mt-1 h-4 w-4 shrink-0 text-maas" aria-hidden="true" />{it}</li>)}
    </ul>
    <Link to={c.href} className="mt-6 inline-flex items-center gap-1.5 text-[15px] font-semibold text-maas hover:underline">{c.cta} <ArrowRight className="h-4 w-4" aria-hidden="true" /></Link>
  </div>
);

const CtaRow = ({ location }) => (
  <div className="mt-8 flex flex-col gap-4 rounded-2xl border border-slate-200 bg-white p-5 sm:flex-row sm:items-center sm:justify-between sm:p-6" data-testid={`${location}-row`}>
    <p className="text-[16px] font-semibold text-ink">Weten wat kunststof of aluminium voor uw woning kost?</p>
    <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:gap-6">
      <QuoteButton location={location} label="Ontvang een prijsindicatie" />
      <CompareLink location={location} label="Offerte vergelijken" />
    </div>
  </div>
);

export default function AluminiumOfKunststof() {
  const t = useSiteTexts("materiaal");
  const advice = t(PAGE_TEXTS.materiaal.blocks.advice);
  return (
    <InfoPage page="materiaal" label="Kunststof of aluminium" eyebrow="Materiaalkeuze" points={MATERIAAL_POINTS} mediaKey="ka_materiaal_hero" nav={MATERIAAL_NAV}
      faqTitle="Vragen over kunststof en aluminium" endTitle="Welk materiaal past bij uw woning?" kbCategory="kozijnen"
      badge={{ icon: Scale, title: "Eerlijk vergeleken", text: "Geen materiaal is altijd beter" }}
      linkHrefs={["/kosten-kunststof-kozijnen-zuid-holland", "/hr++-glas-isolatieglas", "/offerte-vergelijken"]}>
      <InfoSection id="in-het-kort" eyebrow="In het kort" title="Geen van beide is altijd beter"
        lead="Kunststof en aluminium isoleren allebei goed en vragen weinig onderhoud. De keuze hangt af van uw woning, de gewenste uitstraling, de maten en uw budget.">
        <div className="grid gap-5 lg:grid-cols-2">
          <ChooseCard c={CHOOSE.kunststof} accent="text-maas" />
          <ChooseCard c={CHOOSE.aluminium} accent="text-gold" />
        </div>
      </InfoSection>
      <InfoSection id="vergelijking" tone="slate" eyebrow="Vergelijking" title="Kunststof en aluminium kozijnen naast elkaar"
        lead="De belangrijkste verschillen op een rij. De precieze waarden hangen af van het profielsysteem, het glas en de uitvoering.">
        <CompareTable body={MAIN_TABLE} caption="Kunststof en aluminium kozijnen vergeleken" testid="materiaal-main-table" />
        <CtaRow location="materiaal-compare" />
      </InfoSection>
      <InfoSection id="themas" eyebrow="Per thema" title="De verschillen uitgelegd">
        <FactorGrid items={THEMES} testid="materiaal-themes" />
      </InfoSection>
      <InfoSection id="profielen" tone="slate" eyebrow="Profielsystemen" title="Zo zijn de profielen opgebouwd">
        <IllustratedSplit mediaKey="ka_materiaal_kunststof" title="Kunststof: meerkamerprofiel" text={KUNSTSTOF_PROFILE} testid="materiaal-kunststof-split" />
        <div className="mt-14"><IllustratedSplit mediaKey="ka_materiaal_aluminium" title="Aluminium: thermisch onderbroken" text={ALUMINIUM_PROFILE} reverse testid="materiaal-aluminium-split" /></div>
      </InfoSection>
      <ProfileSystemsSection productKeys={["kunststof_kozijnen", "aluminium_kozijnen"]} onderwerp="kunststof-kozijnen" />
      <InfoSection id="toepassingen" eyebrow="Toepassingen" title="Welk materiaal voor welke toepassing?">
        <CompareTable body={APPLICATIONS_TABLE} caption="Toepassingen van kunststof en aluminium" testid="materiaal-applications-table" />
      </InfoSection>
      <InfoSection id="prijzen" tone="slate" eyebrow="Prijzen" title="Kunststof en aluminium: wat betekent het voor de prijs?"
        lead="Wij tonen alleen indicatieve bedragen die wij zelf hebben gecontroleerd en goedgekeurd. Bij elk bedrag staat wat er is inbegrepen.">
        <MaterialPrices />
        <CtaRow location="materiaal-prices" />
      </InfoSection>
      <section aria-labelledby="materiaal-advies-title" className="section" data-testid="materiaal-advice">
        <div className="container-tight grid gap-8 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <h2 id="materiaal-advies-title" className="h2">{advice.title}</h2>
            <TextBody text={advice.body} className="mt-5" />
          </div>
          <div className="card flex flex-col gap-4 p-6 lg:col-span-5">
            <p className="text-[18px] font-semibold text-ink">Persoonlijk advies</p>
            <p className="text-[15px] leading-relaxed text-slate-600">Stuur een paar foto's van uw woning mee; dan adviseren wij gericht welk materiaal past.</p>
            <QuoteButton location="materiaal-advice" label="Vraag advies en een prijsindicatie aan" />
          </div>
        </div>
      </section>
    </InfoPage>
  );
}
